import React from 'react';
import { formatDate, itemStatusLabel, palletLabel, shipperLabel } from './utils';
import DwellCell from './cells/DwellCell';
import ItemStatusCell from './cells/ItemStatusCell';
import OnHandCell from './cells/OnHandCell';
import StockLevelCell from './cells/StockLevelCell';
import StockStatusCell from './cells/StockStatusCell';

const dashed = ({ getValue }) => getValue() || '-';

export const parentColumns = (accountType) => [
    { accessorKey: 'sku', header: 'SKU', size: 150 },
    ...(accountType === 'carrier'
        ? [
              {
                  id: 'shipper',
                  header: 'Shipper',
                  size: 160,
                  accessorFn: (row) => shipperLabel(row),
                  meta: { filter: { type: 'multi-select' } },
              },
          ]
        : []),
    { accessorKey: 'description', header: 'Item', size: 200, cell: dashed },
    {
        id: 'max_dwell',
        header: 'Max Dwell',
        size: 130,
        accessorFn: (row) => row.max_dwell_days,
        cell: DwellCell,
    },
    {
        id: 'on_hand_count',
        header: 'Delivery On Hand',
        size: 140,
        accessorFn: (row) => row.on_hand_count,
        cell: OnHandCell,
    },
    // Display columns: the bar and the pill group read several fields each, so
    // there is no single value to accessor. DataTable's header only asks these
    // for getCanSort/getIsSorted/getCanFilter, which a display column answers.
    { id: 'stock_level', header: 'Delivery Stock Level', size: 180, enableSorting: false, cell: StockLevelCell },
    { id: 'stock_status', header: 'Stock Status', size: 140, enableSorting: false, cell: StockStatusCell },
];

export const childColumns = (accountType) => [
    {
        id: 'serial_number',
        header: 'Serial Number',
        size: 170,
        accessorFn: (row) => row.item?.serial_number,
        cell: dashed,
    },
    { id: 'line_id', header: 'Line Item', size: 120, accessorFn: (row) => row.item?.line_id, cell: dashed },
    {
        id: 'warehouse_location',
        header: 'Warehouse Location',
        size: 180,
        accessorFn: (row) => row.item?.pallet?.warehouse_location,
        cell: dashed,
    },
    { id: 'pallet', header: 'Pallet', size: 150, accessorFn: (row) => palletLabel(row.item?.pallet) },
    // Who is holding it is only a question for a shipper: a carrier is looking
    // at its own warehouse.
    ...(accountType !== 'carrier'
        ? [
              {
                  id: 'warehouse_name',
                  header: 'Warehouse',
                  size: 180,
                  accessorFn: (row) => row.warehouse_name,
                  cell: dashed,
              },
              {
                  id: 'carrier',
                  header: 'Carrier',
                  size: 160,
                  accessorFn: (row) => row.carrier_name,
                  cell: dashed,
                  meta: { filter: { type: 'multi-select' } },
              },
          ]
        : []),
    {
        id: 'received',
        header: 'Received',
        size: 140,
        accessorFn: (row) => row.received_into_warehouse_time,
        cell: ({ getValue }) => formatDate(getValue()),
    },
    {
        id: 'dwell',
        header: 'Dwell',
        size: 110,
        accessorFn: (row) => row.dwell_days,
        // Same threshold colouring as the parent's Max Dwell.
        cell: DwellCell,
    },
    {
        id: 'status',
        header: 'Status',
        // Wider than the other child columns: "Return - Pending Put Away" is
        // the longest label and wrapping it made the row height jump.
        size: 190,
        // The label, not the raw stage: the multi-select filter lists whatever
        // this returns, and the labels are what the user reads off the column.
        accessorFn: (row) => itemStatusLabel(row),
        cell: ItemStatusCell,
        meta: { filter: { type: 'multi-select' } },
    },
];
